<template>
  <div class="company-page">

    <!-- =====================================================
         HEADER
    ====================================================== -->

    <DefaultHeader
      :title="company?.name || 'Компания'"
      subtitle="Профиль компании"
      class="fixed-header"
      :offset="700"
    />


    <!-- =====================================================
         LOADING
    ====================================================== -->

    <CompanySkeleton v-if="isLoading" />


    <!-- =====================================================
         CONTENT
    ====================================================== -->

    <main
      v-else-if="company"
      class="company-container"
    >

      <div class="company-layout">

        <!-- =================================================
             LEFT COLUMN
        ================================================== -->

        <div class="company-main">

          <!-- ===============================================
               HERO
          ================================================ -->

          <section class="company-card company-hero">

            <div class="company-cover">

              <img
                v-if="company?.urlOfBackground"
                :src="company.urlOfBackground"
                alt=""
                class="company-cover-image"
                :class="{ loaded: isBackgroundLoaded }"
                @load="isBackgroundLoaded = true"
              />


              <div
                v-if="!isBackgroundLoaded && company?.urlOfBackground"
                class="cover-skeleton"
              ></div>


              <div class="company-cover-overlay"></div>

            </div>


            <div class="company-hero-body">

              <!-- LOGO -->

              <div class="company-logo-wrapper">

                <img
                  v-if="company?.urlOfLogo"
                  :src="company.urlOfLogo"
                  alt=""
                  class="company-logo"
                />


                <div
                  v-else
                  class="company-logo-fallback"
                >
                  {{ companyInitial }}
                </div>

              </div>


              <!-- SUBSCRIBE -->

              <button
                type="button"
                class="subscribe-button"
                :class="{ subscribed: isSubscribed }"
                :disabled="isLoadingSubscription"
                @click="toggleCompanySubscription"
              >

                <span
                  v-if="isLoadingSubscription"
                  class="loader"
                ></span>


                <template v-else>

                  <svg
                    v-if="!isSubscribed"
                    viewBox="0 0 18 18"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M9 4V14"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />

                    <path
                      d="M4 9H14"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                    />
                  </svg>


                  <svg
                    v-else
                    viewBox="0 0 18 18"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M4 9.2L7.1 12.1L14 5.8"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>


                  {{
                    isSubscribed
                      ? "Вы подписаны"
                      : "Подписаться"
                  }}

                </template>

              </button>


              <!-- INFO -->

              <div class="company-identity">

                <div class="section-eyebrow">
                  Компания
                </div>


                <h1 class="company-name">
                  {{ company?.name || "Название компании" }}
                </h1>


                <div class="company-meta">

                  <span class="location-tag">

                    <svg
                      viewBox="0 0 18 18"
                      fill="none"
                    >
                      <path
                        d="M9 15C9 15 13 11.5 13 8.1C13 5.65 11.2 4 9 4C6.8 4 5 5.65 5 8.1C5 11.5 9 15 9 15Z"
                        stroke="currentColor"
                        stroke-width="1.35"
                      />

                      <circle
                        cx="9"
                        cy="8"
                        r="1.4"
                        stroke="currentColor"
                        stroke-width="1.2"
                      />
                    </svg>

                    {{
                      getRegionNameById(
                        company.regionId
                      )
                    }}

                  </span>


                  <span
                    v-if="company?.specifics"
                    class="specifics-tag"
                  >
                    {{ company.specifics }}
                  </span>

                </div>

              </div>

            </div>


            <!-- ABOUT -->

            <div class="hero-about">

              <div class="hero-about-title">
                О компании
              </div>


              <p class="hero-about-description">
                {{
                  company?.aboutCompany
                  || "Компания пока не добавила описание."
                }}
              </p>

            </div>

          </section>


          <!-- ===============================================
               CONTACTS
          ================================================ -->

          <section class="contact-grid">

            <button
              type="button"
              class="contact-card"
              :class="{ disabled: !company?.email }"
              @click="openEmail(company?.email)"
            >

              <div class="contact-icon">
                <img
                  src="../assets/email-press.png"
                  alt=""
                />
              </div>


              <div class="contact-copy">

                <div class="contact-label">
                  Email
                </div>

                <div class="contact-value">
                  {{
                    company?.email
                    || "Не указано"
                  }}
                </div>

              </div>


              <div class="contact-arrow">
                <svg
                  viewBox="0 0 18 18"
                  fill="none"
                >
                  <path
                    d="M6.5 4.5L11 9L6.5 13.5"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>

            </button>


            <button
              type="button"
              class="contact-card"
              :class="{ disabled: !company?.website }"
              @click="openWebsite(company?.website)"
            >

              <div class="contact-icon website-icon">
                <img
                  src="../assets/web-press.png"
                  alt=""
                />
              </div>


              <div class="contact-copy">

                <div class="contact-label">
                  Website
                </div>

                <div class="contact-value">
                  {{
                    company?.website
                    || "Не указано"
                  }}
                </div>

              </div>


              <div class="contact-arrow">
                <svg
                  viewBox="0 0 18 18"
                  fill="none"
                >
                  <path
                    d="M6.5 4.5L11 9L6.5 13.5"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>

            </button>

          </section>


          <!-- ===============================================
               OFFICE
          ================================================ -->

          <section class="company-card office-card">

            <div class="card-heading">

              <div class="heading-icon office-icon">

                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M5 19V8L12 4L19 8V19"
                    stroke="currentColor"
                    stroke-width="1.55"
                    stroke-linejoin="round"
                  />

                  <path
                    d="M9 19V14H15V19"
                    stroke="currentColor"
                    stroke-width="1.55"
                  />

                  <path
                    d="M8 10H8.01M12 10H12.01M16 10H16.01"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                </svg>

              </div>


              <div>

                <div class="section-eyebrow">
                  Локация
                </div>

                <h2 class="section-title">
                  Офис
                </h2>

                <p class="section-description">
                  {{
                    company?.companyLocation?.address
                    || "Адрес офиса не указан"
                  }}
                </p>

              </div>

            </div>


            <GoogleMap
              v-if="
                company?.companyLocation?.latitude &&
                company?.companyLocation?.longitude
              "
              class="map-wrapper"
              :lat="company.companyLocation.latitude"
              :lng="company.companyLocation.longitude"
              :logo-url="company.urlOfLogo || ''"
            />


            <div
              v-else
              class="map-empty"
            >

              <svg
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M12 20C12 20 18 14.8 18 9.8C18 6.5 15.3 4 12 4C8.7 4 6 6.5 6 9.8C6 14.8 12 20 12 20Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                />

                <circle
                  cx="12"
                  cy="10"
                  r="2"
                  stroke="currentColor"
                  stroke-width="1.5"
                />
              </svg>

              <span>
                Компания пока не указала точку на карте
              </span>

            </div>

          </section>


          <!-- ===============================================
               SOCIAL
          ================================================ -->

          <section class="company-card social-card">

            <div class="card-heading">

              <div class="heading-icon social-heading-icon">

                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="8"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />

                  <path
                    d="M4.5 12H19.5"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />

                  <path
                    d="M12 4C14.4 6.2 15.5 8.8 15.5 12C15.5 15.2 14.4 17.8 12 20"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />

                  <path
                    d="M12 4C9.6 6.2 8.5 8.8 8.5 12C8.5 15.2 9.6 17.8 12 20"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />
                </svg>

              </div>


              <div>

                <div class="section-eyebrow">
                  Контакты
                </div>

                <h2 class="section-title">
                  Социальные сети
                </h2>

                <p class="section-description">
                  Официальные профили компании
                </p>

              </div>

            </div>


            <div class="social-icons">

              <template
                v-for="item in SOCIAL_ICONS"
                :key="item.key"
              >

                <a
                  v-if="socialLinks[item.key]"
                  :href="socialLinks[item.key]"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="social-button"
                  :title="item.key"
                >
                  <img
                    :src="item.icon"
                    class="social-icon"
                    alt=""
                  />
                </a>


                <div
                  v-else
                  class="social-button inactive"
                  :title="item.key"
                >
                  <img
                    :src="item.icon"
                    class="social-icon"
                    alt=""
                  />
                </div>

              </template>

            </div>

          </section>


          <!-- ===============================================
               RATERS
          ================================================ -->

          <section class="company-card raters-section">

            <div class="card-heading">

              <div class="heading-icon people-icon">

                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    cx="9"
                    cy="9"
                    r="3"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />

                  <path
                    d="M4 19C4.6 15.8 6.3 14 9 14C11.7 14 13.4 15.8 14 19"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />

                  <path
                    d="M15.5 7.2C17.2 7.4 18.5 8.7 18.5 10.3C18.5 11.8 17.5 13 16 13.4"
                    stroke="currentColor"
                    stroke-width="1.4"
                    stroke-linecap="round"
                  />

                  <path
                    d="M16.5 15C18.4 15.4 19.6 16.8 20 19"
                    stroke="currentColor"
                    stroke-width="1.4"
                    stroke-linecap="round"
                  />
                </svg>

              </div>


              <div class="heading-copy">

                <div class="section-eyebrow">
                  Команда
                </div>

                <h2 class="section-title">
                  Работали или работают здесь
                </h2>

                <p class="section-description">
                  Люди, которые отметили эту компанию в своём профиле
                </p>

              </div>



              <div class="team-heading-actions">

                <!-- Количество сотрудников -->

                <span class="section-count">
                  {{ company?.raters?.length || 0 }}
                </span>

                <!-- Посмотреть штатную структуру -->

                <button
                  type="button"
                  class="staff-structure-button"
                  @click="openStaffStructure"
                >
                  <svg
                    class="staff-structure-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <rect
                      x="9"
                      y="3"
                      width="6"
                      height="5"
                      rx="1.3"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />

                    <rect
                      x="2"
                      y="16"
                      width="6"
                      height="5"
                      rx="1.3"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />

                    <rect
                      x="9"
                      y="16"
                      width="6"
                      height="5"
                      rx="1.3"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />

                    <rect
                      x="16"
                      y="16"
                      width="6"
                      height="5"
                      rx="1.3"
                      stroke="currentColor"
                      stroke-width="1.6"
                    />

                    <path
                      d="M12 8V12M5 16V12H19V16M12 12V16"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                  <span>
                    Посмотреть структуру
                  </span>

                  <svg
                    class="staff-structure-arrow"
                    viewBox="0 0 20 20"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M4 10H16M16 10L11 5M16 10L11 15"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>

                </div>

            </div>


            <template v-if="company?.raters?.length">

              <div class="raters-scroll-wrapper">

                <button
                  type="button"
                  class="scroll-btn left"
                  @click="scrollRaters('left')"
                >
                  <svg
                    viewBox="0 0 18 18"
                    fill="none"
                  >
                    <path
                      d="M11 4.5L6.5 9L11 13.5"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>


                <div
                  ref="ratersContainer"
                  class="raters-scroll"
                >

                  <Rater
                    v-for="rater in company.raters"
                    :key="rater.id"
                    :rater="rater"
                    class="rater-item"
                    @click="openRaterProfile"
                  />

                </div>


                <button
                  type="button"
                  class="scroll-btn right"
                  @click="scrollRaters('right')"
                >
                  <svg
                    viewBox="0 0 18 18"
                    fill="none"
                  >
                    <path
                      d="M7 4.5L11.5 9L7 13.5"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>

              </div>

            </template>


            <div
              v-else
              class="raters-empty"
            >

              <div class="empty-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    cx="12"
                    cy="9"
                    r="3"
                    stroke="currentColor"
                    stroke-width="1.5"
                  />

                  <path
                    d="M6 19C6.7 15.8 8.7 14 12 14C15.3 14 17.3 15.8 18 19"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />
                </svg>
              </div>


              <div>

                <div class="empty-title">
                  Пока здесь никого нет
                </div>

                <div class="empty-description">
                  Сотрудники ещё не отметили компанию в своих профилях.
                </div>

              </div>

            </div>

          </section>


          <!-- ===============================================
               RATING / REVIEWS
          ================================================ -->

          <section class="company-card reviews-section">

            <div class="card-heading">

              <div class="heading-icon review-icon">

                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M12 4L14.3 8.7L19.5 9.5L15.8 13.2L16.7 18.5L12 16L7.3 18.5L8.2 13.2L4.5 9.5L9.7 8.7L12 4Z"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linejoin="round"
                  />
                </svg>

              </div>


              <div>

                <div class="section-eyebrow">
                  Репутация
                </div>

                <h2 class="section-title">
                  Рейтинг и отзывы
                </h2>

                <p class="section-description">
                  Оценки людей, которые знакомы с компанией
                </p>

              </div>

            </div>


            <!-- RATING SUMMARY -->

            <div class="rating-summary">

              <div class="rating-score">
                {{
                  company?.rating?.toFixed(1)
                  || "—"
                }}
              </div>


              <div class="rating-summary-copy">

                <div class="rating-label">
                  Общий рейтинг
                </div>

                <div class="rating-count">

                  <template v-if="reviewsCount">
                    {{ reviewsCount }}
                    {{
                      reviewsCount === 1
                        ? "отзыв"
                        : "отзывов"
                    }}
                  </template>

                  <template v-else>
                    Пока нет оценок
                  </template>

                </div>

              </div>

            </div>


            <!-- RATING CATEGORIES -->

            <ul class="rating-categories">

              <li
                v-for="item in ratingItems"
                :key="item.label"
                class="rating-item"
              >

                <span class="rating-item-label">
                  {{ item.label }}
                </span>


                <span class="rating-item-value">

                  <img
                    src="../assets/blue-star.png"
                    alt=""
                    class="star-icon"
                  />

                  {{
                    item.value.toFixed(1)
                  }}

                </span>

              </li>

            </ul>


            <!-- REVIEW ACTION -->

            <div class="reviews-divider"></div>


            <div class="reviews-heading">

              <div>

                <div class="reviews-subtitle">
                  Отзывы
                </div>

                <div class="reviews-description">
                  Поделитесь своим опытом работы или взаимодействия с компанией.
                </div>

              </div>


              <button
                type="button"
                class="review-add-button"
                @click="openReviewDrawer"
              >
                Добавить отзыв
              </button>

            </div>


            <div
              v-if="!reviewsCount"
              class="no-reviews-placeholder"
            >

              <img
                src="../assets/empty-reviews.png"
                alt=""
              />


              <div>

                <div class="no-reviews-title">
                  Здесь пока тихо
                </div>

                <p>
                  Станьте первым, кто оставит отзыв.
                  При необходимости его можно отправить анонимно.
                </p>

              </div>

            </div>

          </section>


          <!-- ===============================================
               ADMIN
          ================================================ -->

          <div
            v-if="isAdmin"
            class="admin-edit-wrapper"
          >

            <button
              type="button"
              class="admin-edit-button"
              @click="editCompany"
            >

              <svg
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M13.8 3.8L16.2 6.2L7 15.4L3.8 16.2L4.6 13L13.8 3.8Z"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linejoin="round"
                />
              </svg>

              Редактировать компанию

            </button>

          </div>

        </div>


        <!-- =================================================
             SIDEBAR
        ================================================== -->

        <aside class="company-sidebar">

          <div class="sidebar-sticky">

            <!-- VACANCIES -->

            <section class="sidebar-section">

              <div class="sidebar-heading">

                <div>

                  <div class="sidebar-eyebrow">
                    Работа
                  </div>

                  <h2>
                    Вакансии
                  </h2>

                </div>


                <span class="sidebar-count">
                  {{ enrichedVacancies.length }}
                </span>

              </div>


              <div
                v-if="enrichedVacancies.length"
                class="sidebar-list vacancy-list"
              >

                <VacancyItem
                  v-for="job in enrichedVacancies"
                  :key="job.id"
                  :vacancy="job"
                  @open-vacancy="goToVacancy"
                />

              </div>


              <NoVacanciesCard v-else />

            </section>

                        <!-- EVENTS -->

                        <section
              v-if="companyEvents.length"
              class="sidebar-section"
            >

              <div class="sidebar-heading">

                <div>

                  <div class="sidebar-eyebrow">
                    Компания
                  </div>

                  <h2>
                    Ивенты
                  </h2>

                </div>


                <span class="sidebar-count">
                  {{ companyEvents.length }}
                </span>

              </div>


              <div class="sidebar-list event-list">

                <CompanyEvent
                  v-for="event in companyEvents"
                  :key="event.id"
                  :event="event"
                  @open-event="goToEvent"
                />

              </div>

            </section>

          </div>

        </aside>

      </div>

    </main>


    <!-- =====================================================
         SNACKBAR
    ====================================================== -->

    <Snackbar
      :message="snackbarMessage"
      :show="showSnackbar"
      :type="snackbarType"
    />


    <!-- =====================================================
         FOOTER
    ====================================================== -->

    <Footer class="fixed-footer" />


    <!-- =====================================================
         MODALS
    ====================================================== -->

    <ReviewCompanyDrawer
      :isOpen="isReviewDrawerOpen"
      :companyId="company?.id"
      :raters="
        company?.raters?.map(
          r => r.userId
        ) || []
      "
      @close="isReviewDrawerOpen = false"
    />


    <PremiumPaywall
      v-if="showPaywall"
      class="force-on-top"
      @close="showPaywall = false"
    />


    <AiCoverLetterPreview
      v-if="showPreview"
      :letter="generatedLetter"
      @close="showPreview = false"
      @send="sendToEmployer"
    />

  </div>
</template>


<script setup>

import {
  computed,
  nextTick,
  onMounted,
  ref
} from "vue";

import {
  useRoute,
  useRouter
} from "vue-router";


import {
  AnalyticsService,
  AnalyticsEvent
} from "../utils/AnalyticsService";


import AiCoverLetterPreview
  from "../components/AiCoverLetterPreview.vue";

import DefaultHeader
  from "../components/CompanyHeader.vue";

import VacancyItem
  from "../components/VacancyCompanyItem.vue";

import Rater
  from "../components/Rater.vue";

import Footer
  from "../components/MiniFooter.vue";

import NoVacanciesCard
  from "../components/NoVacanciesCard.vue";

import Snackbar
  from "../components/Snackbar.vue";

import GoogleMap
  from "../components/GoogleMap.vue";

import ReviewCompanyDrawer
  from "../views/ReviewCompanyDrawer.vue";

import PremiumPaywall
  from "../views/Paywall.vue";

import CompanyEvent
  from "../components/CompanyEvent.vue";

import CompanySkeleton
  from "../components/CompanySkeleton.vue";


import CompanyService
  from "../api/CompanyService.js";

import PremiumService
  from "../api/PremiumService.js";

import FollowService
  from "../api/FollowService.js";

import RegionService
  from "../utils/RegionService.js";


import iconFacebook
  from "../assets/facebook.png";

import iconTwitter
  from "../assets/twitter.png";

import iconInstagram
  from "../assets/instagram.png";

import iconLinkedIn
  from "../assets/linkedin-profile.png";

import iconTelegram
  from "../assets/telegram-profile.png";

import iconYouTube
  from "../assets/youtube-profile.png";


/* ============================================================
   ROUTER
============================================================ */

const router =
  useRouter();

const route =
  useRoute();


/* ============================================================
   COMPANY
============================================================ */

const company =
  ref(null);

const isLoading =
  ref(true);

const isBackgroundLoaded =
  ref(false);


/* ============================================================
   SUBSCRIPTION
============================================================ */

const isSubscribed =
  ref(false);

const isLoadingSubscription =
  ref(false);


/* ============================================================
   MODALS
============================================================ */

const isReviewDrawerOpen =
  ref(false);

const showPaywall =
  ref(false);

const showPreview =
  ref(false);

const generatedLetter =
  ref(null);

const isGenerating =
  ref(false);


/* ============================================================
   RATERS
============================================================ */

const ratersContainer =
  ref(null);


/* ============================================================
   ADMIN
============================================================ */

const isAdmin =
  ref(false);


/* ============================================================
   SNACKBAR
============================================================ */

const showSnackbar =
  ref(false);

const snackbarMessage =
  ref("");

const snackbarType =
  ref("success");


let snackbarTimer =
  null;


/* ============================================================
   SOCIAL
============================================================ */

const SOCIAL_ICONS = [

  {
    key: "facebook",
    icon: iconFacebook
  },

  {
    key: "twitter",
    icon: iconTwitter
  },

  {
    key: "instagram",
    icon: iconInstagram
  },

  {
    key: "linkedin",
    icon: iconLinkedIn
  },

  {
    key: "telegram",
    icon: iconTelegram
  },

  {
    key: "youtube",
    icon: iconYouTube
  }

];


const PLATFORM_BUILDERS = {

  facebook:
    value =>
      `https://facebook.com/${value}`,

  twitter:
    value =>
      `https://x.com/${value}`,

  instagram:
    value =>
      `https://instagram.com/${value}`,

  linkedin:
    value => {

      if (
        /^https?:\/\//i.test(value)
      ) {
        return value;
      }


      if (
        /^(company|in|school)\//i.test(
          value
        )
      ) {

        return (
          `https://www.linkedin.com/${value}`
        );

      }


      return (
        `https://www.linkedin.com/company/${value}`
      );

    },

  telegram:
    value =>
      `https://t.me/${value.replace(/^@/, "")}`,

  youtube:
    value => {

      if (
        /^https?:\/\//i.test(value)
      ) {
        return value;
      }


      if (
        /^UC[A-Za-z0-9_-]{22}$/.test(
          value
        )
      ) {

        return (
          `https://www.youtube.com/channel/${value}`
        );

      }


      if (
        /^@/.test(value)
      ) {

        return (
          `https://www.youtube.com/${value}`
        );

      }


      return (
        `https://www.youtube.com/@${value}`
      );

    }

};


/* ============================================================
   COMPANY INITIAL
============================================================ */

const companyInitial =
  computed(() => {

    const name =
      String(
        company.value?.name || "H"
      ).trim();


    return (
      name[0]
        ?.toUpperCase()
      || "H"
    );

  });


/* ============================================================
   RATING
============================================================ */

const ratingItems =
  computed(() => {

    const value =
      company.value;


    return [

      {
        label:
          "Культура компании",

        value:
          Number(
            value?.companyCulture
            ?? 0
          )
      },

      {
        label:
          "Компенсации и бенефиты",

        value:
          Number(
            value?.compensationBenefits
            ?? 0
          )
      },

      {
        label:
          "Менеджмент",

        value:
          Number(
            value?.management
            ?? 0
          )
      },

      {
        label:
          "Профессиональное развитие",

        value:
          Number(
            value?.professionalSkills
            ?? 0
          )
      },

      {
        label:
          "Work-life balance",

        value:
          Number(
            value?.worklifeBalance
            ?? 0
          )
      }

    ];

  });


const reviewsCount =
  computed(() => {

    return (
      company.value?.reviews?.length
      ?? 0
    );

  });


/* ============================================================
   VACANCIES / EVENTS
============================================================ */

const enrichedVacancies =
  computed(() => {

    if (
      !Array.isArray(
        company.value?.vacancies
      )
    ) {
      return [];
    }


    return company.value.vacancies.map(
      vacancy => ({

        ...vacancy,

        company_logo_url:
          company.value?.urlOfLogo,

        company_name:
          company.value?.name,

        company_specifics:
          company.value?.specifics

      })
    );

  });


const companyEvents =
  computed(() => {

    return Array.isArray(
      company.value?.events
    )
      ? company.value.events
      : [];

  });


/* ============================================================
   SOCIAL LINKS
============================================================ */

function sanitizeUrl(urlValue) {

  if (
    !urlValue
    || typeof urlValue !== "string"
    || !urlValue.trim()
  ) {
    return null;
  }


  try {

    const url =
      new URL(
        urlValue,
        "https://example.com"
      );


    const validProtocol =
      url.protocol === "http:"
      || url.protocol === "https:";


    if (!validProtocol) {
      return null;
    }


    if (
      url.origin
      === "https://example.com"
    ) {
      return null;
    }


    return url.toString();


  } catch {

    return null;

  }

}


function normalizeLink(
  platform,
  value
) {

  if (
    !value
    || typeof value !== "string"
    || !value.trim()
  ) {
    return null;
  }


  const raw =
    value.trim();


  if (
    /^https?:\/\//i.test(raw)
  ) {

    return sanitizeUrl(raw);

  }


  const cleaned =
    raw
      .replace(/^\/+/, "")
      .replace(/^www\./i, "");


  const builder =
    PLATFORM_BUILDERS[
      platform
    ];


  if (!builder) {
    return null;
  }


  return sanitizeUrl(
    builder(cleaned)
  );

}


const socialLinks =
  computed(() => {

    const social =
      company.value?.social
      || {};


    return {

      facebook:
        normalizeLink(
          "facebook",
          social.facebook_id
        ),

      twitter:
        normalizeLink(
          "twitter",
          social.twitter_id
        ),

      instagram:
        normalizeLink(
          "instagram",
          social.instagram_id
        ),

      linkedin:
        normalizeLink(
          "linkedin",
          social.linkedin_id
        ),

      telegram:
        normalizeLink(
          "telegram",
          social.telegram_id
        ),

      youtube:
        normalizeLink(
          "youtube",
          social.youtube_id
        )

    };

  });


/* ============================================================
   REGION
============================================================ */

function getRegionNameById(
  regionId
) {

  return (
    RegionService.handleRegion(
      regionId
    )
    || "Регион не указан"
  );

}


/* ============================================================
   SNACKBAR
============================================================ */

function showSubscriptionToast(
  message,
  type = "success"
) {

  snackbarMessage.value =
    message;

  snackbarType.value =
    type;

  showSnackbar.value =
    true;


  if (snackbarTimer) {

    clearTimeout(
      snackbarTimer
    );

  }


  snackbarTimer =
    setTimeout(() => {

      showSnackbar.value =
        false;

    }, 3000);

}


/* ============================================================
   SUBSCRIBE
============================================================ */

async function toggleCompanySubscription() {

  const companyId =
    company.value?.id
    || route.params.companyId;


  if (
    !companyId
    || isLoadingSubscription.value
  ) {
    return;
  }


  isLoadingSubscription.value =
    true;


  try {

    const result =
      await FollowService
        .toggleFollowCompany(
          companyId
        );


    if (
      result === "subscribed"
    ) {

      isSubscribed.value =
        true;


      showSubscriptionToast(
        "Вы подписались на компанию!"
      );

    }

    else if (
      result === "unsubscribed"
    ) {

      isSubscribed.value =
        false;


      showSubscriptionToast(
        "Вы отписались от компании."
      );

    }

    else {

      showSubscriptionToast(
        "Не удалось изменить подписку",
        "error"
      );

    }


    AnalyticsService.log(
      AnalyticsEvent.FOLLOW_COMPANY
    );


  } catch (error) {

    console.error(
      "❌ Ошибка подписки:",
      error
    );


    showSubscriptionToast(
      "Не удалось изменить подписку",
      "error"
    );


  } finally {

    isLoadingSubscription.value =
      false;

  }

}


/* ============================================================
   RATERS SCROLL
============================================================ */

function scrollRaters(
  direction
) {

  const container =
    ratersContainer.value;


  if (!container) {
    return;
  }


  container.scrollBy({

    left:
      direction === "left"
        ? -300
        : 300,

    behavior:
      "smooth"

  });

}


/* ============================================================
   LINKS
============================================================ */

function openEmail(
  email
) {

  if (!email) {
    return;
  }


  window.location.href =
    `mailto:${email}`;

}


function openWebsite(
  url
) {

  if (!url) {
    return;
  }


  const normalized =
    /^https?:\/\//i.test(url)
      ? url
      : `https://${url}`;


  window.open(
    normalized,
    "_blank",
    "noopener,noreferrer"
  );

}


/* ============================================================
   REVIEW
============================================================ */

function openReviewDrawer() {

  isReviewDrawerOpen.value =
    true;

}


/* ============================================================
   ROUTING
============================================================ */

function goToVacancy(
  vacancy
) {

  if (
    !vacancy?.id
  ) {

    console.warn(
      "⚠️ Vacancy ID отсутствует:",
      vacancy
    );

    return;

  }


  router.push({

    name:
      "Vacancy",

    params: {
      vacancyId:
        vacancy.id
    }

  });

}


function openRaterProfile(
  id
) {

  if (!id) {
    return;
  }


  router.push({

    name:
      "User",

    params: {
      id
    }

  });

}


function goToEvent(
  event
) {

  if (
    !event?.id
  ) {
    return;
  }


  router.push({

    name:
      "event",

    params: {
      eventId:
        event.id
    }

  });

}


/* ============================================================
   ADMIN
============================================================ */

function editCompany() {

  if (
    !company.value?.id
  ) {
    return;
  }


  router.push({

    name:
      "AdminEditCompany",

    params: {
      companyId:
        company.value.id
    }

  });

}


/* ============================================================
   AI COVER LETTER
============================================================ */

async function openAiCoverLetter() {

  if (
    isGenerating.value
  ) {
    return;
  }


  showPreview.value =
    false;

  generatedLetter.value =
    null;

  isGenerating.value =
    true;


  try {

    const companyId =
      company.value?.id
      || route.params.companyId;


    if (!companyId) {

      throw new Error(
        "Company ID отсутствует"
      );

    }


    const letter =
      await PremiumService
        .generateCoverLetter(
          companyId
        );


    if (!letter) {

      throw new Error(
        "Сервис не вернул сопроводительное письмо"
      );

    }


    generatedLetter.value =
      letter;

    showPreview.value =
      true;


  } catch (error) {

    console.error(
      "❌ Ошибка AI генерации:",
      error
    );


    showSubscriptionToast(
      error?.response?.data?.error
      || error?.message
      || "Не удалось сгенерировать сопроводительное письмо",
      "error"
    );


  } finally {

    isGenerating.value =
      false;

  }

}


async function sendToEmployer() {

  if (
    !company.value?.id
    || !generatedLetter.value
  ) {
    return;
  }


  isGenerating.value =
    true;


  try {

    await PremiumService
      .sendCoverLetter(
        company.value.id,
        generatedLetter.value
      );


    showSubscriptionToast(
      "Письмо отправлено работодателю!"
    );


    showPreview.value =
      false;


  } catch (error) {

    console.error(
      "❌ Ошибка отправки письма:",
      error
    );


    showSubscriptionToast(
      "Не удалось отправить письмо",
      "error"
    );


  } finally {

    AnalyticsService.log(
      AnalyticsEvent
        .SEND_USER_COVER_AI_LETTER
    );


    isGenerating.value =
      false;

  }

}


/* ============================================================
   LOAD
============================================================ */

onMounted(
  async () => {

    const adminFlag =
      localStorage.getItem(
        "is_admin"
      );


    isAdmin.value =
      adminFlag === "true";


    window.scrollTo({
      top: 0,
      behavior: "instant"
    });


    const companyId =
      route.params.companyId;


    try {

      company.value =
        await CompanyService
          .fetchCompanyById(
            companyId
          );


      isSubscribed.value =
        company.value?.isFollowed === true
        || company.value?.is_followed === true;


      if (
        !company.value
          ?.urlOfBackground
      ) {

        isBackgroundLoaded.value =
          true;

      }


      await nextTick();


      requestAnimationFrame(
        () => {

          if (
            ratersContainer.value
          ) {

            ratersContainer
              .value
              .scrollLeft = 0;

          }

        }
      );


    } catch (error) {

      console.error(
        "❌ Ошибка при загрузке компании:",
        error
      );


      showSubscriptionToast(
        "Не удалось загрузить компанию",
        "error"
      );


    } finally {

      AnalyticsService.log(
        AnalyticsEvent.VIEW_COMPANY
      );


      isLoading.value =
        false;

    }

  }
);

function openStaffStructure() {
  const companyId =
    company.value?.id || route.params.companyId;

  if (!companyId) {
    return;
  }

  router.push({
    name: "CompanyStructure",
    params: {
      companyId
    }
  });
}

</script>

<style scoped>

/* ============================================================
   PAGE
============================================================ */

.company-page {
  min-height: 100vh;

  font-family:
    "Eastman-Regular",
    sans-serif;

  background: #f4f6f8;
}


.company-container {
  min-height: 100vh;

  box-sizing: border-box;

  padding:
    86px
    0
    110px;

  color: #252a31;

  background:
    #f6f6f6;
}


/* ============================================================
   MAIN LAYOUT
============================================================ */

.company-layout {
  width:
    min(
      1180px,
      calc(100% - 40px)
    );

  margin:
    0
    auto;

  display: grid;

  grid-template-columns:
    minmax(0, 1fr)
    388px;

  align-items: start;

  gap: 22px;
}


.company-main {
  min-width: 0;

  display: flex;
  flex-direction: column;

  gap: 16px;
}


/* ============================================================
   BASE CARD
============================================================ */

.company-card {
  position: relative;

  width: 100%;

  box-sizing: border-box;

  overflow: hidden;

  background: #ffffff;

  border:
    1px solid
    rgba(20, 35, 55, 0.075);

  border-radius: 24px;

  box-shadow:
    0 14px 38px
    rgba(15, 23, 42, 0.045);
}


/* ============================================================
   HERO
============================================================ */

.company-hero {
  overflow: visible;

  padding: 0;

  border-radius: 26px;
}


/* ============================================================
   COVER
============================================================ */

.company-cover {
  position: relative;

  width: 100%;
  height: 230px;

  overflow: hidden;

  border-radius:
    25px
    25px
    0
    0;

  background:
    linear-gradient(
      135deg,
      #e4eaf1,
      #e9e5f3
    );
}


.company-cover-image {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;

  opacity: 0;

  transform:
    scale(1.01);

  transition:
    opacity 0.35s ease,
    transform 0.5s ease;
}


.company-cover-image.loaded {
  opacity: 1;

  transform:
    scale(1);
}


.company-cover-overlay {
  position: absolute;

  inset: 0;

  pointer-events: none;

  background:
    linear-gradient(
      180deg,
      rgba(8, 12, 18, 0.01) 35%,
      rgba(8, 12, 18, 0.13) 100%
    );
}


/* ============================================================
   COVER SKELETON
============================================================ */

.cover-skeleton {
  position: absolute;

  inset: 0;

  z-index: 2;

  overflow: hidden;

  background: #e8ebef;
}


.cover-skeleton::after {
  content: "";

  position: absolute;

  inset: 0;

  transform:
    translateX(-100%);

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.78),
      transparent
    );

  animation:
    coverShimmer
    1.3s
    ease-in-out
    infinite;
}


@keyframes coverShimmer {
  to {
    transform:
      translateX(100%);
  }
}


/* ============================================================
   HERO BODY
============================================================ */

.company-hero-body {
  position: relative;

  display: flex;
  flex-direction: column;
  align-items: flex-start;

  min-height: 0;

  padding:
    0
    24px
    20px;
}


.company-logo-wrapper {
  position: relative;

  z-index: 6;

  width: 98px;
  height: 98px;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  margin-top: -41px;

  overflow: hidden;

  background: #ffffff;

  border:
    5px solid
    #ffffff;

  border-radius: 25px;

  box-shadow:
    0
    14px
    32px
    rgba(15, 23, 42, 0.16);
}


.company-logo {
  width: 100%;
  height: 100%;

  display: block;

  object-fit: cover;

  border-radius: 20px;
}


.company-logo-fallback {
  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #487ad0;

  background:
    linear-gradient(
      135deg,
      #edf3fb,
      #efecfa
    );

  border-radius: 20px;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 29px;
}


/* ============================================================
   IDENTITY
============================================================ */

.company-identity {
  width: 100%;

  min-width: 0;

  padding-top: 14px;
}

.section-eyebrow,
.sidebar-eyebrow {
  margin: 0 0 5px;

  color: #989da5;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  line-height: 1.2;

  letter-spacing: 0.075em;

  text-transform: uppercase;
}


.company-name {
  margin: 0;

  color: #282d34;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 20px;

  line-height: 1.25;

  letter-spacing: -0.32px;

  overflow-wrap: anywhere;
}


.company-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  gap: 7px;

  margin-top: 10px;
}

/* ============================================================
   META TAGS
============================================================ */

.location-tag,
.specifics-tag {
  min-height: 28px;

  display: inline-flex;
  align-items: center;

  box-sizing: border-box;

  padding:
    0
    9px;

  border-radius: 9px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  line-height: 1;
}


.location-tag {
  gap: 5px;

  color: #6482ad;

  background:
    #f4f7fb;

  border:
    1px solid
    rgba(70, 105, 160, 0.08);
}


.location-tag svg {
  width: 12px;
  height: 12px;

  flex-shrink: 0;
}


.specifics-tag {
  max-width: 340px;

  color: #7d70ac;

  background:
    #f7f4fc;

  border:
    1px solid
    rgba(115, 90, 185, 0.07);

  overflow: hidden;

  white-space: nowrap;

  text-overflow: ellipsis;
}


/* ============================================================
   SUBSCRIBE
============================================================ */


.subscribe-button {
  position: absolute;

  top: 18px;
  right: 24px;

  min-width: 126px;
  min-height: 38px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 6px;

  margin: 0;

  padding:
    0
    14px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3187ee,
      #4d6be7
    );

  border: none;

  border-radius: 11px;

  box-shadow:
    0
    8px
    18px
    rgba(45, 110, 220, 0.20);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  cursor: pointer;

  white-space: nowrap;

  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease,
    opacity 0.18s ease;
}

.subscribe-button svg {
  width: 13px;
  height: 13px;

  flex-shrink: 0;
}


.subscribe-button:hover:not(:disabled) {
  transform:
    translateY(-1px);

  box-shadow:
    0
    11px
    23px
    rgba(45, 110, 220, 0.26);
}


.subscribe-button.subscribed {
  color: #3978d8;

  background:
    #f3f7fd;

  border:
    1px solid
    rgba(55, 120, 215, 0.13);

  box-shadow: none;
}


.subscribe-button.subscribed:hover:not(:disabled) {
  background:
    #edf4fd;

  box-shadow: none;
}


.subscribe-button:disabled {
  opacity: 0.62;

  cursor: default;

  transform: none;
}


/* ============================================================
   LOADER
============================================================ */

.loader {
  width: 14px;
  height: 14px;

  box-sizing: border-box;

  border:
    2px solid
    rgba(255, 255, 255, 0.35);

  border-top-color:
    #ffffff;

  border-radius: 50%;

  animation:
    spin
    0.72s
    linear
    infinite;
}


.subscribe-button.subscribed
.loader {
  border-color:
    rgba(55, 120, 215, 0.20);

  border-top-color:
    #3978d8;
}


@keyframes spin {
  to {
    transform:
      rotate(360deg);
  }
}


/* ============================================================
   HERO ABOUT
============================================================ */

.hero-about {
  margin:
    0
    24px;

  padding:
    17px
    0
    22px;

  border-top:
    1px solid
    rgba(25, 40, 60, 0.055);
}


.hero-about-title {
  margin: 0;

  color: #666d76;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
}


.hero-about-description {
  max-width: 94%;

  margin:
    7px
    0
    0;

  color: #838993;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 10px;

  line-height: 1.58;

  white-space: pre-wrap;
}


/* ============================================================
   CONTACTS
============================================================ */

.contact-grid {
  display: grid;

  grid-template-columns:
    repeat(
      2,
      minmax(0, 1fr)
    );

  gap: 12px;
}


.contact-card {
  min-width: 0;
  min-height: 78px;

  display: grid;

  grid-template-columns:
    39px
    minmax(0, 1fr)
    27px;

  align-items: center;

  gap: 11px;

  box-sizing: border-box;

  padding: 12px;

  text-align: left;

  color: inherit;

  background: #ffffff;

  border:
    1px solid
    rgba(20, 35, 55, 0.075);

  border-radius: 18px;

  box-shadow:
    0
    10px
    26px
    rgba(15, 23, 42, 0.035);

  cursor: pointer;

  transition:
    transform 0.18s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease;
}


.contact-card:hover:not(.disabled) {
  transform:
    translateY(-2px);

  border-color:
    rgba(60, 115, 195, 0.13);

  box-shadow:
    0
    15px
    32px
    rgba(15, 23, 42, 0.07);
}


.contact-card.disabled {
  opacity: 0.48;

  cursor: default;
}


.contact-icon {
  width: 39px;
  height: 39px;

  display: flex;
  align-items: center;
  justify-content: center;

  background:
    #f3f7fc;

  border-radius: 11px;
}


.website-icon {
  background:
    #f7f4fc;
}


.contact-icon img {
  width: 19px;
  height: 19px;

  object-fit: contain;
}


.contact-copy {
  min-width: 0;
}


.contact-label {
  color: #9a9fa6;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 7px;

  letter-spacing: 0.055em;

  text-transform: uppercase;
}


.contact-value {
  margin-top: 4px;

  color: #444a52;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  overflow: hidden;

  white-space: nowrap;

  text-overflow: ellipsis;
}


.contact-arrow {
  width: 27px;
  height: 27px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #7289ab;

  background:
    #f6f8fb;

  border-radius: 8px;
}


.contact-arrow svg {
  width: 12px;
  height: 12px;
}


/* ============================================================
   REGULAR CONTENT CARDS
============================================================ */

.office-card,
.social-card,
.raters-section,
.reviews-section {
  padding: 23px;
}


.card-heading {
  display: flex;
  align-items: flex-start;

  gap: 12px;

  margin-bottom: 18px;
}


.heading-copy {
  min-width: 0;

  flex: 1;
}


.heading-icon {
  width: 41px;
  height: 41px;

  flex:
    0
    0
    41px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 12px;
}


.heading-icon svg {
  width: 18px;
  height: 18px;
}


.office-icon {
  color: #4c7bc8;

  background:
    #f2f6fb;
}


.social-heading-icon {
  color: #7765bd;

  background:
    #f6f3fb;
}


.people-icon {
  color: #448f6c;

  background:
    #f1f8f4;
}


.review-icon {
  color: #c99130;

  background:
    #fbf7ee;
}


.section-title {
  margin: 0;

  color: #30353c;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 14px;

  font-weight: 500;

  line-height: 1.3;
}


.section-description {
  margin:
    5px
    0
    0;

  color: #969ba4;

  font-size: 9px;

  line-height: 1.45;
}


.section-count {
  min-width: 26px;
  height: 26px;

  margin-left: auto;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  padding:
    0
    7px;

  color: #6c83a4;

  background:
    #f3f6fa;

  border-radius: 999px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;
}


/* ============================================================
   MAP
============================================================ */

.map-wrapper {
  width: 100%;

  display: block;

  overflow: hidden;

  border-radius: 16px;
}


.map-empty {
  min-height: 150px;

  display: flex;
  flex-direction: column;

  align-items: center;
  justify-content: center;

  gap: 9px;

  box-sizing: border-box;

  color: #989da5;

  background:
    #f7f8fa;

  border:
    1px dashed
    rgba(30, 45, 65, 0.09);

  border-radius: 15px;

  font-size: 8px;

  text-align: center;
}


.map-empty svg {
  width: 25px;
  height: 25px;

  color: #7c91ae;
}


/* ============================================================
   SOCIAL
============================================================ */

.social-icons {
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  gap: 9px;
}


.social-button {
  width: 42px;
  height: 42px;

  display: flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  background:
    #f7f8fa;

  border:
    1px solid
    rgba(25, 40, 60, 0.055);

  border-radius: 12px;

  text-decoration: none;

  transition:
    transform 0.18s ease,
    background-color 0.18s ease,
    border-color 0.18s ease;
}


.social-button:not(.inactive):hover {
  transform:
    translateY(-2px);

  background:
    #ffffff;

  border-color:
    rgba(55, 110, 190, 0.11);
}


.social-button.inactive {
  opacity: 0.26;
}


.social-icon {
  width: 19px;
  height: 19px;

  display: block;

  object-fit: contain;
}


/* ============================================================
   RATERS
============================================================ */

.raters-scroll-wrapper {
  position: relative;

  min-width: 0;
}


.raters-scroll {
  display: flex;

  gap: 12px;

  overflow-x: auto;

  padding:
    3px
    4px
    7px;

  scroll-behavior: smooth;

  scrollbar-width: none;

  overscroll-behavior-x: contain;
}


.raters-scroll::-webkit-scrollbar {
  display: none;
}


.rater-item {
  flex:
    0
    0
    auto;
}


.scroll-btn {
  position: absolute;

  top: 50%;

  z-index: 10;

  width: 30px;
  height: 30px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  color: #657993;

  background:
    rgba(255, 255, 255, 0.94);

  border:
    1px solid
    rgba(25, 40, 60, 0.075);

  border-radius: 10px;

  box-shadow:
    0
    7px
    18px
    rgba(15, 23, 42, 0.09);

  transform:
    translateY(-50%);

  cursor: pointer;

  transition:
    transform 0.18s ease,
    background-color 0.18s ease;
}


.scroll-btn:hover {
  transform:
    translateY(-50%)
    scale(1.04);
}


.scroll-btn svg {
  width: 13px;
  height: 13px;
}


.scroll-btn.left {
  left: -8px;
}


.scroll-btn.right {
  right: -8px;
}


/* ============================================================
   RATERS EMPTY
============================================================ */

.raters-empty {
  min-height: 94px;

  display: flex;
  align-items: center;

  gap: 12px;

  box-sizing: border-box;

  padding: 14px;

  background:
    #f8f9fa;

  border:
    1px dashed
    rgba(25, 40, 60, 0.075);

  border-radius: 14px;
}


.empty-icon {
  width: 38px;
  height: 38px;

  flex:
    0
    0
    38px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #7d91ad;

  background:
    #f0f4f9;

  border-radius: 11px;
}


.empty-icon svg {
  width: 18px;
  height: 18px;
}


.empty-title,
.no-reviews-title {
  color: #555c65;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
}


.empty-description {
  margin-top: 4px;

  color: #989da5;

  font-size: 8px;

  line-height: 1.42;
}


/* ============================================================
   RATING
============================================================ */

.rating-summary {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  gap: 8px;

  box-sizing: border-box;

  padding:
    22px
    16px;

  text-align: center;

  background:
    linear-gradient(
      145deg,
      #f6f9fd,
      #f8f7fc
    );

  border:
    1px solid
    rgba(55, 105, 185, 0.07);

  border-radius: 18px;
}


.rating-score {
  width: 82px;
  height: 82px;

  flex:
    0
    0
    82px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: #3876d3;

  background:
    #edf4fd;

  border:
    1px solid
    rgba(55, 105, 185, 0.08);

  border-radius: 50%;

  font-family:
    "Eastman-Bold",
    sans-serif;

  font-size: 25px;

  line-height: 1;

  box-shadow:
    0
    10px
    24px
    rgba(55, 105, 185, 0.08);
}


.rating-summary-copy {
  display: flex;
  flex-direction: column;
  align-items: center;

  text-align: center;
}


.rating-label {
  color: #424851;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;
}


.rating-count {
  margin-top: 4px;

  color: #969ba4;

  font-size: 8px;
}

/* ============================================================
   RATING LIST
============================================================ */

.rating-categories {
  width: 100%;

  display: flex;
  flex-direction: column;

  box-sizing: border-box;

  margin:
    14px
    0
    0;

  padding: 0;

  list-style: none;
}

.rating-item {
  min-width: 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  gap: 5px;

  padding:
    12px
    8px;

  text-align: center;

  border-bottom:
    1px solid
    rgba(25, 40, 60, 0.05);
}


.rating-item:last-child {
  border-bottom: none;
}


.rating-item-label {
  width: 100%;

  color: #60666f;

  font-family:
    "Eastman-Regular",
    sans-serif;

  font-size: 9px;

  line-height: 1.35;

  text-align: center;
}


.rating-item-value {
  min-width: 0;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 4px;

  color: #45618a;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;
}


.star-icon {
  width: 12px;
  height: 12px;

  object-fit: contain;
}


/* ============================================================
   REVIEWS
============================================================ */

.reviews-divider {
  height: 1px;

  margin:
    6px
    0
    16px;

  background:
    rgba(25, 40, 60, 0.055);
}


.reviews-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 15px;
}


.reviews-subtitle {
  color: #4b525b;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 10px;
}


.reviews-description {
  max-width: 405px;

  margin-top: 4px;

  color: #989da5;

  font-size: 8px;

  line-height: 1.42;
}


.review-add-button {
  min-height: 37px;

  flex-shrink: 0;

  padding:
    0
    13px;

  color: #ffffff;

  background:
    linear-gradient(
      135deg,
      #3187ee,
      #4b6ae6
    );

  border: none;

  border-radius: 10px;

  box-shadow:
    0
    6px
    15px
    rgba(45, 110, 220, 0.15);

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;

  cursor: pointer;

  transition:
    transform 0.18s ease,
    box-shadow 0.18s ease;
}


.review-add-button:hover {
  transform:
    translateY(-1px);

  box-shadow:
    0
    9px
    19px
    rgba(45, 110, 220, 0.20);
}


.no-reviews-placeholder {
  display: flex;
  align-items: center;

  gap: 13px;

  box-sizing: border-box;

  margin-top: 15px;

  padding: 14px;

  background:
    #f8f9fa;

  border-radius: 14px;
}


.no-reviews-placeholder img {
  width: 50px;
  height: 50px;

  flex-shrink: 0;

  object-fit: contain;

  opacity: 0.70;
}


.no-reviews-placeholder p {
  max-width: 430px;

  margin:
    4px
    0
    0;

  color: #989da5;

  font-size: 8px;

  line-height: 1.48;
}


/* ============================================================
   ADMIN
============================================================ */

.admin-edit-wrapper {
 margin-left: 300px;
  justify-content: flex-end;
}


.admin-edit-button {
  min-height: 39px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 7px;

  padding:
    0
    14px;

  color: #b66d2b;

  background:
    #fdf7f1;

  border:
    1px solid
    rgba(215, 125, 40, 0.12);

  border-radius: 11px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 9px;

  cursor: pointer;

  transition:
    background-color 0.18s ease,
    transform 0.18s ease;
}


.admin-edit-button:hover {
  transform:
    translateY(-1px);

  background:
    #fbf1e8;
}


.admin-edit-button svg {
  width: 14px;
  height: 14px;
}


/* ============================================================
   SIDEBAR
============================================================ */

.company-sidebar {
  min-width: 0;

  background: transparent;
}


.sidebar-sticky {
  position: sticky;

  top: 82px;

  display: flex;
  flex-direction: column;

  gap: 15px;

  overflow: visible;

  padding-right: 2px;
}


.sidebar-section {
  box-sizing: border-box;

  padding:
    17px
    7px
    7px;

  background:
    rgba(255, 255, 255, 0.54);

  border:
    1px solid
    rgba(20, 35, 55, 0.05);

  border-radius: 20px;
}


.sidebar-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 12px;

  padding:
    0
    8px
    12px;
}


.sidebar-heading h2 {
  margin: 0;

  color: #343a42;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 13px;

  font-weight: 500;
}


.sidebar-count {
  min-width: 26px;
  height: 26px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  box-sizing: border-box;

  padding:
    0
    7px;

  color: #6b81a1;

  background:
    #f2f5f9;

  border-radius: 999px;

  font-family:
    "Eastman-Medium",
    sans-serif;

  font-size: 8px;
}


/* ============================================================
   SIDEBAR LISTS
============================================================ */

.sidebar-list {
  min-width: 0;

  display: flex;
  flex-direction: column;

  gap: 11px;
}


.vacancy-list > *,
.event-list > * {
  width: 100% !important;

  height: auto !important;

  min-height: 0 !important;

  flex-shrink: 0;
}


/* ============================================================
   HEADER / FOOTER
============================================================ */

.fixed-header {
  position: fixed;

  top: 0;
  left: 0;
  right: 0;

  z-index: 1000;
}


.fixed-footer {
  position: fixed;

  bottom: 0;
  left: 0;
  right: 0;

  z-index: 1000;
}


/* ============================================================
   MODALS
============================================================ */

.force-on-top {
  position: fixed !important;

  z-index: 99999 !important;
}


/* ============================================================
   DARK
============================================================ */

html[data-theme="dark"]
.company-page,
html[data-theme="dark"]
.company-container {
  background: #17181b;

  color: #f1f2f4;
}


html[data-theme="dark"]
.company-card {
  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(255, 255, 255, 0.065);

  box-shadow:
    0
    17px
    45px
    rgba(0, 0, 0, 0.20);
}


html[data-theme="dark"]
.company-cover {
  background:
    linear-gradient(
      135deg,
      #2d3138,
      #2c2936
    );
}


html[data-theme="dark"]
.cover-skeleton {
  background: #2a2d32;
}


html[data-theme="dark"]
.cover-skeleton::after {
  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.07),
      transparent
    );
}


html[data-theme="dark"]
.company-logo-wrapper {
  background: #22242a;

  border-color: #22242a;

  box-shadow:
    0
    14px
    34px
    rgba(0, 0, 0, 0.38);
}


html[data-theme="dark"]
.company-logo-fallback {
  color: #8db4f2;

  background:
    linear-gradient(
      135deg,
      #29313c,
      #302d3b
    );
}


html[data-theme="dark"]
.company-name,
html[data-theme="dark"]
.section-title,
html[data-theme="dark"]
.sidebar-heading h2 {
  color: #f0f1f3;
}


html[data-theme="dark"]
.location-tag {
  color: #96b5e4;

  background:
    rgba(75, 125, 205, 0.09);

  border-color:
    rgba(95, 145, 225, 0.10);
}


html[data-theme="dark"]
.specifics-tag {
  color: #b1a5dc;

  background:
    rgba(115, 90, 195, 0.09);

  border-color:
    rgba(135, 110, 215, 0.10);
}


html[data-theme="dark"]
.subscribe-button.subscribed {
  color: #92baff;

  background:
    rgba(65, 120, 210, 0.09);

  border-color:
    rgba(100, 155, 240, 0.13);
}


html[data-theme="dark"]
.hero-about {
  border-color:
    rgba(255, 255, 255, 0.055);
}


html[data-theme="dark"]
.hero-about-title,
html[data-theme="dark"]
.rating-label,
html[data-theme="dark"]
.reviews-subtitle {
  color: #d4d7dc;
}


html[data-theme="dark"]
.hero-about-description,
html[data-theme="dark"]
.section-description,
html[data-theme="dark"]
.rating-item-label,
html[data-theme="dark"]
.reviews-description {
  color: #858a93;
}


html[data-theme="dark"]
.contact-card {
  background:
    linear-gradient(
      145deg,
      #22242a,
      #1d1f24
    );

  border-color:
    rgba(255, 255, 255, 0.065);

  box-shadow:
    0
    11px
    28px
    rgba(0, 0, 0, 0.15);
}


html[data-theme="dark"]
.contact-value {
  color: #dedfe2;
}


html[data-theme="dark"]
.contact-icon,
html[data-theme="dark"]
.contact-arrow,
html[data-theme="dark"]
.social-button {
  background:
    rgba(255, 255, 255, 0.035);

  border-color:
    rgba(255, 255, 255, 0.05);
}


html[data-theme="dark"]
.map-empty,
html[data-theme="dark"]
.raters-empty,
html[data-theme="dark"]
.no-reviews-placeholder {
  color: #858a93;

  background:
    rgba(255, 255, 255, 0.025);

  border-color:
    rgba(255, 255, 255, 0.065);
}


html[data-theme="dark"]
.empty-icon {
  color: #9ab4dc;

  background:
    rgba(80, 130, 210, 0.08);
}


html[data-theme="dark"]
.empty-title,
html[data-theme="dark"]
.no-reviews-title {
  color: #d2d5da;
}


html[data-theme="dark"]
.rating-summary {
  background:
    linear-gradient(
      145deg,
      rgba(65, 120, 215, 0.085),
      rgba(105, 85, 205, 0.05)
    );

  border-color:
    rgba(95, 145, 230, 0.10);
}


html[data-theme="dark"]
.rating-score {
  color: #91b9fb;

  background:
    rgba(65, 120, 215, 0.10);
}


html[data-theme="dark"]
.rating-item {
  border-color:
    rgba(255, 255, 255, 0.055);
}


html[data-theme="dark"]
.rating-item-value {
  color: #9bb8e5;
}


html[data-theme="dark"]
.reviews-divider {
  background:
    rgba(255, 255, 255, 0.055);
}


html[data-theme="dark"]
.admin-edit-button {
  color: #e2a56b;

  background:
    rgba(210, 130, 55, 0.07);

  border-color:
    rgba(220, 145, 70, 0.11);
}


html[data-theme="dark"]
.sidebar-section {
  background:
    rgba(255, 255, 255, 0.022);

  border-color:
    rgba(255, 255, 255, 0.05);
}


html[data-theme="dark"]
.sidebar-count {
  color: #9bb6dd;

  background:
    rgba(80, 125, 195, 0.08);
}


html[data-theme="dark"]
.scroll-btn {
  color: #c7cad0;

  background:
    rgba(37, 39, 44, 0.96);

  border-color:
    rgba(255, 255, 255, 0.065);

  box-shadow:
    0
    8px
    20px
    rgba(0, 0, 0, 0.28);
}


/* ============================================================
   TABLET
============================================================ */

@media (max-width: 1100px) {

  .company-layout {
    width:
      min(
        820px,
        calc(100% - 32px)
      );

    grid-template-columns:
      minmax(0, 1fr);
  }


  .company-sidebar {
    width: 100%;
  }


  .sidebar-sticky {
    position: static;

    max-height: none;

    overflow: visible;
  }


  .sidebar-section {
    padding:
      17px
      9px
      9px;
  }

}


/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 700px) {

  .company-container {
    padding:
      70px
      0
      28px;
  }


  .company-layout {
    width:
      calc(100% - 20px);

    gap: 12px;
  }


  .company-main {
    gap: 12px;
  }


  .company-card {
    border-radius: 20px;
  }


  /* HERO */

  .company-hero {
    border-radius: 20px;
  }


  .company-cover {
    height: 180px;

    border-radius:
      19px
      19px
      0
      0;
  }

  .company-hero-body {
    padding:
      0
      16px
      15px;
  }


  .company-logo-wrapper {
    width: 82px;
    height: 82px;

    margin-top: -34px;

    border-width: 4px;

    border-radius: 21px;
  }



  .company-logo,
  .company-logo-fallback {
    border-radius: 17px;
  }


  .company-logo-fallback {
    font-size: 23px;
  }


  .company-identity {
    padding-top: 12px;
  }


  .company-name {
    font-size: 17px;
  }


  .company-meta {
    margin-top: 8px;
  }


  .specifics-tag {
    max-width: 100%;
  }


  .subscribe-button {
    position: static;

    width: 100%;

    min-height: 41px;

    margin-top: 14px;
  }

  .hero-about {
    margin:
      0
      16px;

    padding:
      15px
      0
      18px;
  }


  .hero-about-description {
    max-width: none;
  }


  /* CONTACTS */

  .contact-grid {
    grid-template-columns: 1fr;
  }


  /* CONTENT */

  .office-card,
  .social-card,
  .raters-section,
  .reviews-section {
    padding:
      19px
      16px;
  }


  .card-heading {
    margin-bottom: 16px;
  }


  /* REVIEWS */

  .reviews-heading {
    flex-direction: column;

    align-items: stretch;
  }


  .review-add-button {
    width: 100%;

    min-height: 40px;
  }


  .rating-summary {
    padding: 12px;
  }


  .rating-score {
    width: 56px;
    height: 56px;

    flex-basis: 56px;

    font-size: 19px;
  }


  .no-reviews-placeholder {
    align-items: flex-start;
  }


  /* RATERS */

  .scroll-btn {
    display: none;
  }


  /* SIDEBAR */

  .sidebar-section {
    border-radius: 18px;
  }


  .fixed-footer {
    position: relative;
  }

}


/* ============================================================
   VERY SMALL
============================================================ */

@media (max-width: 430px) {

  .company-cover {
    height: 165px;
  }


  .company-hero-body {
    grid-template-columns:
      74px
      minmax(0, 1fr);
  }


  .company-logo-wrapper {
    width: 74px;
    height: 74px;

    margin-top: -29px;

    border-radius: 19px;
  }


  .company-logo,
  .company-logo-fallback {
    border-radius: 15px;
  }


  .company-name {
    font-size: 16px;
  }


  .company-meta {
    align-items: flex-start;

    flex-direction: column;
  }


  .location-tag,
  .specifics-tag {
    max-width: 100%;
  }


  .card-heading {
    gap: 10px;
  }


  .heading-icon {
    width: 38px;
    height: 38px;

    flex-basis: 38px;
  }


  .rating-item {
    gap: 8px;
  }


  .rating-item-label {
    max-width: 75%;
  }


  .no-reviews-placeholder img {
    width: 44px;
    height: 44px;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (prefers-reduced-motion: reduce) {

  .company-cover-image,
  .subscribe-button,
  .contact-card,
  .social-button,
  .review-add-button,
  .admin-edit-button,
  .scroll-btn {
    transition: none;
  }


  .loader,
  .cover-skeleton::after {
    animation: none;
  }

}

html[data-theme="dark"] .heading-icon {
  border:
    1px solid
    rgba(255, 255, 255, 0.055);
}


/* OFFICE */

html[data-theme="dark"] .office-icon {
  color: #8faacb;

  background:
    rgba(115, 145, 185, 0.09);
}


/* SOCIAL */

html[data-theme="dark"] .social-heading-icon {
  color: #aaa0c7;

  background:
    rgba(135, 115, 175, 0.09);
}


/* PEOPLE */

html[data-theme="dark"] .people-icon {
  color: #8eb8a5;

  background:
    rgba(85, 145, 115, 0.09);
}


/* REVIEWS */

html[data-theme="dark"] .review-icon {
  color: #c5aa7b;

  background:
    rgba(175, 135, 75, 0.09);
}


/* ============================================================
   TEAM — STAFF STRUCTURE ACTION
============================================================ */

.team-heading-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;

  flex-shrink: 0;

  gap: 9px;

  margin-left: auto;
}

/* Убираем автоматический отступ счётчика внутри группы */

.team-heading-actions .section-count {
  margin-left: 0;
}

/* ============================================================
   STAFF STRUCTURE BUTTON
============================================================ */

.staff-structure-button {
  min-height: 32px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  gap: 7px;

  padding: 0 11px;

  color: #2173e5;

  background: rgba(33, 115, 229, 0.07);

  border: 1px solid rgba(33, 115, 229, 0.13);
  border-radius: 10px;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 9px;
  font-weight: 500;

  line-height: 1.2;

  white-space: nowrap;

  cursor: pointer;

  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.staff-structure-button:hover {
  color: #ffffff;

  background: #2173e5;

  border-color: #2173e5;

  box-shadow:
    0 6px 17px rgba(33, 115, 229, 0.18);

  transform: translateY(-1px);
}

.staff-structure-button:active {
  transform: scale(0.98);
}

.staff-structure-button:focus-visible {
  outline: 2px solid #2173e5;
  outline-offset: 3px;
}

.staff-structure-icon {
  width: 15px;
  height: 15px;

  flex-shrink: 0;
}

.staff-structure-arrow {
  width: 13px;
  height: 13px;

  flex-shrink: 0;

  transition: transform 0.2s ease;
}

.staff-structure-button:hover .staff-structure-arrow {
  transform: translateX(2px);
}

/* ============================================================
   DARK THEME
============================================================ */

html[data-theme="dark"] .staff-structure-button {
  color: #8dbbff;

  background: rgba(70, 135, 235, 0.10);

  border-color: rgba(100, 160, 245, 0.16);
}

html[data-theme="dark"] .staff-structure-button:hover {
  color: #ffffff;

  background: #2173e5;

  border-color: #2173e5;
}

/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 700px) {

  .raters-section > .card-heading {
    flex-wrap: wrap;
  }

  .team-heading-actions {
    width: 100%;

    justify-content: flex-start;

    margin-left: 0;
    margin-top: 5px;
  }

  .staff-structure-button {
    min-height: 36px;
    font-size: 10px;
  }

}

@media (max-width: 430px) {

  .staff-structure-button {
    flex: 1;

    padding: 0 9px;

    white-space: normal;

    text-align: center;
  }

}


</style>